import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowUpCircle, 
  Download, 
  Filter,
  Upload,
  WalletCards,
  ArrowDownCircle,
  CheckCircle
} from 'lucide-react';
import { useUpgradeStore } from '../stores/upgradeStore';
import { useOrganizersStore } from '../stores/organizersStore';
import { RequestFilters } from '../components/roomUpgrade/RequestFilters';
import { RequestList } from '../components/roomUpgrade/RequestList';
import { RequestStats } from '../components/roomUpgrade/RequestStats';
import { UpgradeRequestCreator } from '../components/roomUpgrade/UpgradeRequestCreator';
import { BulkUpgradeModal } from '../components/roomUpgrade/BulkUpgradeModal';
import { BulkAnswerModal } from '../components/roomUpgrade/BulkAnswerModal';
import { BulkAnswerOrganizerModal } from '../components/roomUpgrade/BulkAnswerOrganizerModal';
import { BatchApprovalModal } from '../components/roomUpgrade/BatchApprovalModal';
import { BatchOrganizerApprovalModal } from '../components/roomUpgrade/BatchOrganizerApprovalModal';
import { DeductionStatsPanel } from '../components/roomUpgrade/DeductionStatsPanel';
import { BulkConfirmRejectModal } from '../components/roomUpgrade/BulkConfirmRejectModal';
import { exportRoomUpgrades } from '../utils/exportRoomUpgrades';

export const RoomUpgradePage = () => {
  const [mode, setMode] = useState<'list' | 'create'>('list');
  const [expandedRequest, setExpandedRequest] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isBulkAnswerModalOpen, setIsBulkAnswerModalOpen] = useState(false);
  const [isBulkAnswerOrganizerModalOpen, setIsBulkAnswerOrganizerModalOpen] = useState(false);
  const [isBatchApprovalModalOpen, setIsBatchApprovalModalOpen] = useState(false);
  const [isBatchOrganizerApprovalModalOpen, setIsBatchOrganizerApprovalModalOpen] = useState(false);
  const [isBulkConfirmRejectModalOpen, setIsBulkConfirmRejectModalOpen] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [organizerFilter, setOrganizerFilter] = useState('all');
  const [hotelFilter, setHotelFilter] = useState('all');
  
  const { 
    requests, 
    loading, 
    initialized, 
    initializeListener 
  } = useUpgradeStore();
  
  const { 
    organizers,
    fetchOrganizers
  } = useOrganizersStore();

  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');

  // Initialize data
  useEffect(() => {
    if (!initialized) {
      initializeListener();
    }
    fetchOrganizers();
  }, [initialized, initializeListener, fetchOrganizers]);

  // Get unique hotels for filter
  const hotels = useMemo(() => {
    const hotelSet = new Set<string>();
    
    Object.values(requests).forEach(req => {
      if (req.fromHotel) {
        hotelSet.add(req.fromHotel);
      }
    });
    
    return Array.from(hotelSet).sort();
  }, [requests]);

  // Filter requests
  const filteredRequests = useMemo(() => {
    return Object.values(requests).filter(req => {
      // Match search term
      const matchesSearch = !searchTerm || 
        req.pilgrimName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.passportNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.guideName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.organizerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.fromHotel.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Match status
      const matchesStatus = statusFilter === 'all' || req.status === statusFilter;
      
      // Match hotel
      const matchesHotel = hotelFilter === 'all' || req.fromHotel === hotelFilter;
      
      // Match organizer
      const matchesOrganizer = organizerFilter === 'all' || req.organizerId === organizerFilter;
      
      return matchesSearch && matchesStatus && matchesHotel && matchesOrganizer;
    }).sort((a, b) => b.createdAt - a.createdAt);
  }, [requests, searchTerm, statusFilter, hotelFilter, organizerFilter]);

  // Calculate requests eligible for batch approval (admin only)
  const batchApprovableRequests = useMemo(() => {
    if (userRole !== 'admin') return [];
    
    return filteredRequests.filter(req => req.status === 'pending');
  }, [filteredRequests, userRole]);

  // Calculate requests eligible for batch deduction (organizer only)
  const batchDeductibleRequests = useMemo(() => {
    if (userRole !== 'organizer') return [];
    
    return filteredRequests.filter(req => 
      (req.status === 'approved' || req.status === 'payment_pending') && 
      req.organizerId === userId
    );
  }, [filteredRequests, userRole, userId]);

  // Check if there are paid upgrades awaiting confirmation or rejection
  const hasPaidUpgrades = useMemo(() => {
    if (userRole !== 'admin') return false;
    
    return Object.values(requests).some(req => req.status === 'paid');
  }, [requests, userRole]);

  // Check if organizer has any approved requests pending payment
  const hasPendingPaymentRequests = useMemo(() => {
    if (userRole !== 'organizer') return false;
    
    return Object.values(requests).some(
      req => (req.status === 'approved' || req.status === 'payment_pending') && 
             req.organizerId === userId
    );
  }, [requests, userRole, userId]);

  // Handle export
  const handleExport = () => {
    exportRoomUpgrades(filteredRequests, {
      searchTerm,
      statusFilter,
      organizerFilter,
      hotelFilter
    });
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white p-6 rounded-xl shadow-sm"
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ArrowUpCircle className="text-primary-500" />
              Room Upgrades
            </h1>
            <p className="text-gray-500 mt-1">Manage room upgrades and change requests</p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            {/* Export Button */}
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <Download size={18} />
              <span className="hidden sm:inline">Export</span>
            </button>
            
            {/* Bulk Upload Button - For guide and organizer */}
            {(userRole === 'guide' || userRole === 'organizer') && (
              <button
                onClick={() => setIsBulkModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors"
              >
                <Upload size={18} />
                <span className="hidden sm:inline">Bulk Upgrade</span>
              </button>
            )}
            
            {/* Bulk Answer Button - For admin only */}
            {userRole === 'admin' && (
              <button
                onClick={() => setIsBulkAnswerModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
              >
                <Upload size={18} />
                <span className="hidden sm:inline">Bulk Answer</span>
              </button>
            )}
            
            {/* Bulk Confirm/Reject Button - For admin only with paid upgrades */}
            {userRole === 'admin' && hasPaidUpgrades && (
              <button
                onClick={() => setIsBulkConfirmRejectModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors"
              >
                <CheckCircle size={18} />
                <span className="hidden sm:inline">Bulk Confirm/Reject</span>
              </button>
            )}
            
            {/* Bulk Answer Organizer Button - For organizers with pending payment requests */}
            {userRole === 'organizer' && hasPendingPaymentRequests && (
              <button
                onClick={() => setIsBulkAnswerOrganizerModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-yellow-50 text-yellow-600 rounded-lg hover:bg-yellow-100 transition-colors"
              >
                <WalletCards size={18} />
                <span className="hidden sm:inline">Bulk Payment</span>
              </button>
            )}
            
            {/* Batch Approval Button - For admin only */}
            {userRole === 'admin' && batchApprovableRequests.length > 0 && (
              <button
                onClick={() => setIsBatchApprovalModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                <ArrowUpCircle size={18} />
                <span className="hidden sm:inline">Batch Approval</span>
                <span className="bg-white text-green-500 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                  {batchApprovableRequests.length}
                </span>
              </button>
            )}
            
            {/* Batch Deduction Button - For organizer only */}
            {userRole === 'organizer' && batchDeductibleRequests.length > 0 && (
              <button
                onClick={() => setIsBatchOrganizerApprovalModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                <WalletCards size={18} />
                <span className="hidden sm:inline">Batch Deduction</span>
                <span className="bg-white text-red-500 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                  {batchDeductibleRequests.length}
                </span>
              </button>
            )}
            
            {/* Create/List Toggle Button */}
            <button
              onClick={() => setMode(mode === 'list' ? 'create' : 'list')}
              className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              {mode === 'list' ? (
                <>
                  <ArrowUpCircle size={18} />
                  <span>Create Request</span>
                </>
              ) : (
                <>
                  <Filter size={18} />
                  <span>View Requests</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Deduction Stats Panel - Shown for both admin and organizer */}
        {(userRole === 'admin' || userRole === 'organizer') && mode === 'list' && (
          <DeductionStatsPanel 
            requests={Object.values(requests)}
            userRole={userRole || 'guest'}
            userId={userId || ''}
            organizers={organizers}
          />
        )}

        {/* Regular Stats Panel - Only in list mode, for requests */}
        {mode === 'list' && Object.keys(requests).length > 0 && (
          <div className="mt-6">
            <RequestStats requests={Object.values(requests)} />
          </div>
        )}
      </motion.div>

      {mode === 'create' ? (
        <UpgradeRequestCreator />
      ) : (
        <>
          {/* Filters */}
          <RequestFilters 
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            organizerFilter={organizerFilter}
            setOrganizerFilter={setOrganizerFilter}
            hotelFilter={hotelFilter}
            setHotelFilter={setHotelFilter}
            organizers={organizers}
            hotels={hotels}
            userRole={userRole || 'guest'}
          />

          {/* Request List */}
          <RequestList 
            requests={filteredRequests}
            expandedRequest={expandedRequest}
            setExpandedRequest={setExpandedRequest}
            isUploading={isUploading}
            userRole={userRole || 'guest'}
          />
        </>
      )}

      {/* Modals */}
      <BulkUpgradeModal 
        isOpen={isBulkModalOpen}
        onClose={() => setIsBulkModalOpen(false)}
      />
      
      <BulkAnswerModal 
        isOpen={isBulkAnswerModalOpen}
        onClose={() => setIsBulkAnswerModalOpen(false)}
      />
      
      <BulkAnswerOrganizerModal
        isOpen={isBulkAnswerOrganizerModalOpen}
        onClose={() => setIsBulkAnswerOrganizerModalOpen(false)}
      />
      
      <BulkConfirmRejectModal
        isOpen={isBulkConfirmRejectModalOpen}
        onClose={() => setIsBulkConfirmRejectModalOpen(false)}
      />
      
      <BatchApprovalModal
        isOpen={isBatchApprovalModalOpen}
        onClose={() => setIsBatchApprovalModalOpen(false)}
        requests={batchApprovableRequests}
      />
      
      <BatchOrganizerApprovalModal
        isOpen={isBatchOrganizerApprovalModalOpen}
        onClose={() => setIsBatchOrganizerApprovalModalOpen(false)}
        requests={batchDeductibleRequests}
      />
    </div>
  );
};